import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

/**
 * Shared layout for service landing pages (/anxiety, /ocd, /stress)
 * Renders: header, hero, content sections, CTA, footer
 */
const ServicePageLayout = ({
  title,
  metaTitle,
  intro,
  badges = [],
  sections = [],
  activeNav,
}) => {
  // Update document title client-side for SEO
  React.useEffect(() => {
    if (metaTitle) document.title = metaTitle;
    return () => {
      document.title = 'Therapy with Ayesha | Psychologist & Therapist';
    };
  }, [metaTitle]);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 flex h-20 items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="https://customer-assets.emergentagent.com/job_wellness-journeys/artifacts/05qbsdun_Generated_image%20%281%29.png"
              alt="Therapy with Ayesha"
              className="h-16 w-auto"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium hover:text-primary">Home</Link>
            <Link to="/services" className="text-sm font-medium hover:text-primary">Services</Link>
            <Link to="/resources" className="text-sm font-medium hover:text-primary">Healing Resources</Link>
            <Link to="/#contact" className="text-sm font-medium hover:text-primary">Contact</Link>
            <Link to="/services">
              <Button size="sm" className="bg-primary hover:bg-primary/90">Book Session</Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="hero-section py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground leading-tight">
              {title}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              {intro}
            </p>
            {badges.length > 0 && (
              <div className="flex flex-wrap gap-3 justify-center pt-2">
                {badges.map((b, i) => (
                  <Badge key={i} variant="secondary" className="px-3 py-1.5">
                    {b}
                  </Badge>
                ))}
              </div>
            )}
            <div className="pt-6">
              <Link to="/services">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-lg px-8 py-6" data-testid="hero-book-session-btn">
                  Book a Session
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <p className="text-sm text-muted-foreground italic mt-3">
                Takes less than 2 minutes. Personal response before the session.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-16">
            {sections.map((sec, i) => (
              <div key={i} className="space-y-4" data-testid={`content-section-${i + 1}`}>
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground">
                  {sec.heading}
                </h2>
                {sec.body && (
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    {sec.body}
                  </p>
                )}
                {sec.points && (
                  <ul className="space-y-3 pt-2">
                    {sec.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span className="text-base text-muted-foreground leading-relaxed">{p}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {sec.pillars && (
                  <div className="grid md:grid-cols-3 gap-4 pt-4">
                    {sec.pillars.map((p, j) => (
                      <Card key={j} className="border-2">
                        <CardContent className="pt-6 space-y-3">
                          <h3 className="text-lg font-serif font-semibold text-foreground">{p.title}</h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-serif font-bold">
              Ready to Begin?
            </h2>
            <p className="text-lg opacity-90">
              Investing in your mental health today, for a better tomorrow.
            </p>
            <Link to="/services">
              <Button size="lg" variant="secondary" className="text-lg px-8 py-6 bg-background text-primary hover:bg-background/90" data-testid="cta-book-session-btn">
                Book a Session
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <p className="text-sm opacity-80 italic">
              Personal response before the session.
            </p>
          </div>
        </div>
      </section>

      {/* Mini Footer */}
      <footer className="bg-card border-t py-10">
        <div className="container mx-auto px-4 text-center space-y-3">
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary">
            ← Back to Home
          </Link>
          <p className="text-xs text-muted-foreground">
            © 2026 Therapy with Ayesha. Ayesha Khaleel, M.A.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ServicePageLayout;
